import { useEffect, useState } from "react"
import { Box, Button, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import Iconify from "minimals-template/components/Iconify"
import { getTreeId } from "routes/schedule/lib/getTreeId"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import {
    applyTailoringToSchedules,
    getApplyTailoringScheduleTargets,
} from "routes/regime/extension-edit/controller/apply-tailoring-to-schedules"
import { ApplyTailoringToSchedulesDialogRow } from "./apply-tailoring-to-schedules-dialog-row"
import { ApplyTailoringToSchedulesScrollArea } from "./apply-tailoring-to-schedules-scroll-area"

const EMPTY_TARGETS = []

export function ApplyTailoringToSchedulesDialog({ currentId, payload, ok, cancel }) {
    const treeId = getTreeId(currentId)
    const [selectedPaths, setSelectedPaths] = useState([])
    const [applying, setApplying] = useState(false)
    const targetsResult = getApplyTailoringScheduleTargets.useResults(
        { treeId, schedulePath: currentId },
        withInitialValue(IS_LOADING)
    )
    const targets = getTargets(targetsResult)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(selectAllTargets, [targetsResult])

    return (
        <>
            <DialogTitle>Apply To Other Schedules</DialogTitle>
            <DialogContent data-cy="apply-tailoring-to-schedules-dialog">
                <Stack spacing={2} sx={{ pt: 1, minWidth: { sm: 620 } }}>
                    {renderTargetCount()}
                    {renderTargets()}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel} disabled={applying} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={applyToSelectedSchedules}
                    disabled={applying || !selectedPaths.length}
                    variant="contained"
                    startIcon={<Iconify icon="mdi:content-copy" />}
                    data-cy="apply-tailoring-to-schedules-confirm"
                >
                    Apply tailoring to schedules
                </Button>
            </DialogActions>
        </>
    )

    function renderTargets() {
        if (targetsResult === IS_LOADING) return <LoadingScreen />
        // null / non-array means the query failed (see the controller): say so instead of the
        // misleading "no schedules match" empty state, which masked server errors from QA (FIQ-1632).
        if (!Array.isArray(targetsResult)) {
            return (
                <Typography variant="body2" color="error" data-cy="apply-tailoring-targets-error">
                    Couldn&apos;t load the matching schedules. Please close this dialog and try again.
                </Typography>
            )
        }
        if (!targets.length) {
            return (
                <Typography variant="body2" color="text.secondary" data-cy="apply-tailoring-no-targets">
                    No other schedules match this original schedule code.
                </Typography>
            )
        }

        return (
            <Box>
                <RequestPageRepeat
                    list={targets}
                    total={targets.length}
                    pageSize={10}
                    showTop
                    showBottom={false}
                    ContentWrapper={ApplyTailoringToSchedulesScrollArea}
                    pass="schedule"
                    keyFn="schedulePath"
                    item={
                        <ApplyTailoringToSchedulesDialogRow selectedPaths={selectedPaths} onToggle={toggleSchedule} />
                    }
                />
            </Box>
        )
    }

    function renderTargetCount() {
        if (targetsResult === IS_LOADING || !targets.length) return null
        const schedules = targets.length === 1 ? "schedule" : "schedules"
        return (
            <Typography variant="caption" color="text.secondary">
                {targets.length} matching {schedules}
            </Typography>
        )
    }

    function selectAllTargets() {
        if (targetsResult === IS_LOADING) return
        const allPaths = []
        for (const target of targets) {
            allPaths.push(target.schedulePath)
        }
        setSelectedPaths(allPaths)
    }

    function toggleSchedule(schedulePath) {
        setSelectedPaths((current) => getToggledPaths(current, schedulePath))
    }

    function getToggledPaths(current, schedulePath) {
        if (current.includes(schedulePath)) {
            return current.filter((path) => path !== schedulePath)
        }
        return [...current, schedulePath]
    }

    async function applyToSelectedSchedules() {
        setApplying(true)
        try {
            const result = await applyTailoringToSchedules({
                treeId,
                sourceSchedulePath: currentId,
                targetSchedulePaths: selectedPaths,
                payload,
            })
            successSnackbar(getSuccessMessage(result))
            ok(result)
        } catch (error) {
            errorSnackbar(error?.message || "Couldn't apply tailoring to the selected schedules")
        } finally {
            setApplying(false)
        }
    }
}

function getTargets(targetsResult) {
    if (targetsResult === IS_LOADING) return EMPTY_TARGETS
    if (!Array.isArray(targetsResult)) return EMPTY_TARGETS
    return targetsResult
}

function getSuccessMessage(result = {}) {
    const applied = result.applied ?? 0
    const schedules = applied === 1 ? "schedule" : "schedules"
    return `Applied tailoring to ${applied} ${schedules}`
}
